/**
 * dat.GUI 控制面板配置。
 * 包含 Camera、background、Display、Model、Backend 五个设置区域。
 */
import * as posedetection from '@tensorflow-models/pose-detection';
import * as tf from '@tensorflow/tfjs-core';

import * as params from './params';

/** 各 WebGL flag 在当前环境下的默认值缓存 */
let TUNABLE_FLAG_DEFAULT_VALUE_MAP;

let enableTrackingController;
let scoreThresholdController;
let colorByPersonController;
let showTrackIdController;
let backgroundTypeController;

/** 背景类型变更回调（由 index 注入，用于刷新工具栏与资源就绪状态） */
let onBackgroundTypeChanged = null;

/** dat.gui 下拉框字符串与真实值的映射（解决类型被转为 string 的问题） */
const stringValueMap = {};

/** 注册背景类型变更回调 */
export function setBackgroundTypeChangeHandler(handler) {
  onBackgroundTypeChanged = handler;
}

/** 运行中锁定/解锁 background type 控件 */
export function setBackgroundTypeLocked(locked) {
  setControllerEnabled(backgroundTypeController, !locked);
}

/** 创建并初始化全部 GUI 面板 */
export async function setupDatGui() {
  const gui = new dat.GUI({width: 300});
  gui.domElement.id = 'gui';

  const cameraFolder = gui.addFolder('Camera');
  const fpsController = cameraFolder.add(params.STATE.camera, 'targetFPS');
  fpsController.onFinishChange((_) => {
    params.STATE.isTargetFPSChanged = true;
  });
  const sizeController = cameraFolder.add(
      params.STATE.camera, 'sizeOption', Object.keys(params.VIDEO_SIZE));
  sizeController.onChange(_ => {
    params.STATE.isSizeOptionChanged = true;
  });
  cameraFolder.open();

  const backgroundFolder = gui.addFolder('background');
  params.STATE.background = {...params.BACKGROUND_CONFIG};
  backgroundTypeController = backgroundFolder.add(
      params.STATE.background, 'type', ['camera', 'img', 'video']);
  backgroundTypeController.onChange(_ => {
    if (params.RUNTIME.running) {
      return;
    }
    params.STATE.isBackgroundChanged = true;
    if (onBackgroundTypeChanged) {
      onBackgroundTypeChanged();
    }
  });
  backgroundFolder.open();

  const displayFolder = gui.addFolder('Display');
  params.STATE.display = {...params.DISPLAY_CONFIG};
  displayFolder.add(params.STATE.display, 'showSkeleton').name('显示骨骼连线');
  displayFolder.add(params.STATE.display, 'showBoundingBox').name('显示边界框');
  colorByPersonController = displayFolder.add(
      params.STATE.display, 'colorByPerson').name('区分线颜色');
  showTrackIdController = displayFolder.add(
      params.STATE.display, 'showTrackId').name('显示追踪 ID');
  displayFolder.open();

  const modelFolder = gui.addFolder('Model');
  params.STATE.model = posedetection.SupportedModels.MoveNet;
  addMoveNetControllers(modelFolder, 'multipose');
  updateTrackingDependentControllers();
  modelFolder.open();

  const backendFolder = gui.addFolder('Backend');
  params.STATE.backend = 'tfjs-webgl';
  await showFlagSettings(backendFolder, params.STATE.backend);
  backendFolder.open();

  return gui;
}

/** enableTracking 未开启时禁用依赖追踪的 Display 控件（dat.gui 0.7.6 无 disable API） */
function setControllerEnabled(controller, enabled) {
  if (controller == null) {
    return;
  }
  const li = controller.domElement;
  li.style.pointerEvents = enabled ? '' : 'none';
  li.style.opacity = enabled ? '' : '0.5';
  const input = li.querySelector('input');
  if (input != null) {
    input.disabled = !enabled;
  }
  // dat.GUI 下拉为 select
  const select = li.querySelector('select');
  if (select != null) {
    select.disabled = !enabled;
  }
}

function updateTrackingDependentControllers() {
  const enabled = !!params.STATE.modelConfig.enableTracking;
  setControllerEnabled(colorByPersonController, enabled);
  setControllerEnabled(showTrackIdController, enabled);
}

/** 添加 MoveNet 模型相关控件：type、自定义模型、阈值、追踪 */
function addMoveNetControllers(modelConfigFolder, type) {
  params.STATE.modelConfig = {...params.MOVENET_CONFIG};
  params.STATE.modelConfig.type = type != null ? type : 'lightning';

  if (params.STATE.modelConfig.type === 'multipose') {
    // multipose 默认开启追踪，否则 pose.id 为空，「显示追踪 ID」无效
    params.STATE.modelConfig.enableTracking = true;
    params.STATE.modelConfig.scoreThreshold = 0.2;
  }

  const typeController = modelConfigFolder.add(
      params.STATE.modelConfig, 'type', ['lightning', 'thunder', 'multipose']);
  typeController.onChange(type => {
    params.STATE.isModelChanged = true;
    if (type === 'multipose') {
      if (enableTrackingController) {
        enableTrackingController.setValue(true);
      }
      if (scoreThresholdController) {
        scoreThresholdController.setValue(0.2);
      }
    } else {
      enableTrackingController.setValue(false);
    }
    updateTrackingDependentControllers();
  });

  const customModelController =
      modelConfigFolder.add(params.STATE.modelConfig, 'customModel');
  customModelController.onFinishChange(_ => {
    params.STATE.isModelChanged = true;
  });

  scoreThresholdController =
      modelConfigFolder.add(params.STATE.modelConfig, 'scoreThreshold', 0, 1);

  enableTrackingController = modelConfigFolder.add(
      params.STATE.modelConfig,
      'enableTracking',
  );
  enableTrackingController.onChange(_ => {
    params.STATE.isModelChanged = true;
    updateTrackingDependentControllers();
  });
}

/** 读取当前环境下各 WebGL flag 的默认值，写入 STATE.flags */
async function initDefaultValueMap() {
  TUNABLE_FLAG_DEFAULT_VALUE_MAP = {};
  params.STATE.flags = {};
  for (const backend in params.BACKEND_FLAGS_MAP) {
    for (let index = 0; index < params.BACKEND_FLAGS_MAP[backend].length;
         index++) {
      const flag = params.BACKEND_FLAGS_MAP[backend][index];
      TUNABLE_FLAG_DEFAULT_VALUE_MAP[flag] = await tf.env().getAsync(flag);
    }
  }

  for (const flag in TUNABLE_FLAG_DEFAULT_VALUE_MAP) {
    if (params.BACKEND_FLAGS_MAP[params.STATE.backend].indexOf(flag) > -1) {
      params.STATE.flags[flag] = TUNABLE_FLAG_DEFAULT_VALUE_MAP[flag];
    }
  }
}

/**
 * 根据 flag 默认值推断 GUI 中可选的调节范围。
 * 例如：默认 false 的布尔 flag 通常不可改为 true。
 */
function getTunableRange(flag) {
  const defaultValue = TUNABLE_FLAG_DEFAULT_VALUE_MAP[flag];
  if (flag === 'WEBGL_FORCE_F16_TEXTURES') {
    return [false, true];
  } else if (flag === 'WEBGL_VERSION') {
    const tunableRange = [];
    for (let value = 1; value <= defaultValue; value++) {
      tunableRange.push(value);
    }
    return tunableRange;
  } else if (flag === 'WEBGL_FLUSH_THRESHOLD') {
    const tunableRange = [-1];
    for (let value = 0; value <= 2; value += 0.25) {
      tunableRange.push(value);
    }
    return tunableRange;
  } else if (typeof defaultValue === 'boolean') {
    return defaultValue ? [false, true] : [false];
  } else if (params.TUNABLE_FLAG_VALUE_RANGE_MAP[flag] != null) {
    return params.TUNABLE_FLAG_VALUE_RANGE_MAP[flag];
  } else {
    return [defaultValue];
  }
}

/** 在 Backend 面板中渲染各 WebGL 调优项（中文名称见 params.TUNABLE_FLAG_NAME_MAP） */
function showBackendFlagSettings(folderController, backendName) {
  const tunableFlags = params.BACKEND_FLAGS_MAP[backendName];
  for (let index = 0; index < tunableFlags.length; index++) {
    const flag = tunableFlags[index];
    const flagName = params.TUNABLE_FLAG_NAME_MAP[flag] || flag;

    const flagValueRange = getTunableRange(flag);
    if (flagValueRange.length < 2) {
      console.warn(
          `The ${flag} is considered as untunable, ` +
          `because its value range is [${flagValueRange}].`);
      continue;
    }

    let flagController;
    if (typeof flagValueRange[0] === 'boolean') {
      flagController = folderController.add(params.STATE.flags, flag);
    } else {
      flagController =
          folderController.add(params.STATE.flags, flag, flagValueRange);

      if (stringValueMap[flag] == null) {
        stringValueMap[flag] = {};
        for (let index = 0; index < flagValueRange.length; index++) {
          const realValue = flagValueRange[index];
          const stringValue = String(flagValueRange[index]);
          stringValueMap[flag][stringValue] = realValue;
        }
      }
      flagController.onFinishChange(stringValue => {
        params.STATE.flags[flag] = stringValueMap[flag][stringValue];
      });
    }
    flagController.name(flagName).onChange(() => {
      params.STATE.isFlagChanged = true;
    });
  }
}

/** 清空并重建 Backend 面板中的 flag 控件 */
async function showFlagSettings(folderController, backendName) {
  await initDefaultValueMap();

  while (folderController.__controllers.length > 0) {
    folderController.remove(
        folderController
            .__controllers[folderController.__controllers.length - 1]);
  }

  showBackendFlagSettings(folderController, backendName);
}
